import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Truck, Receipt, AlertTriangle, Calendar, TrendingUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import PushNotificationToggle from '@/components/PushNotificationToggle';
import { SkeletonCard, SkeletonVehicleList } from '@/components/SkeletonCard';
import QuickActionsWidget from '@/components/QuickActionsWidget';
import { useTranslation } from 'react-i18next';

const Dashboard = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    totalVehicles: 0,
    activeVehicles: 0,
    maintenanceVehicles: 0,
    pendingReceipts: 0,
    tuvExpiring: 0
  });
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [vehiclesRes] = await Promise.all([
        axios.get('/vehicles')
      ]);
      
      const vehicleData = vehiclesRes.data.vehicles || [];
      setVehicles(vehicleData);
      
      // Calculate stats
      const now = new Date();
      const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      
      setStats({
        totalVehicles: vehicleData.length,
        activeVehicles: vehicleData.filter(v => v.status === 'in_use').length,
        maintenanceVehicles: vehicleData.filter(v => v.status === 'maintenance').length,
        pendingReceipts: 0,
        tuvExpiring: vehicleData.filter(v => {
          const tuvDate = new Date(v.tuv_expiry);
          return tuvDate <= thirtyDaysFromNow;
        }).length
      });
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div className="space-y-2 animate-pulse">
            <div className="h-12 w-64 bg-muted rounded"></div>
            <div className="h-4 w-48 bg-muted rounded"></div>
          </div>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-muted rounded-lg"></div>
          </div>
        </div>

        {/* Skeleton Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>

        {/* Skeleton Vehicle List */}
        <Card className="border-border">
          <CardHeader>
            <div className="h-6 w-48 bg-muted rounded animate-pulse"></div>
          </CardHeader>
          <CardContent>
            <SkeletonVehicleList />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8" data-testid="dashboard">
      {/* Header - Responsive Stack on Mobile */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fadeIn">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black tracking-tight uppercase text-foreground mb-1 sm:mb-2">
            {t('dashboard.title')}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">Flottenübersicht und Status</p>
        </div>
        <div className="flex items-center gap-3 self-start sm:self-auto">
          <PushNotificationToggle />
        </div>
      </div>

      {/* Quick Actions Widget - Only for Drivers */}
      <QuickActionsWidget />

      {/* Stats Grid - 2x2 on mobile, 4 columns on desktop */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
        <Card className="border-border transition-colors duration-200 animate-fadeIn delay-100" data-testid="total-vehicles-card">
          <CardHeader className="flex flex-row items-center justify-between pb-1 sm:pb-2 p-3 sm:p-6">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">{t('dashboard.vehicles.total')}</CardTitle>
            <Truck className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
            <div className="text-2xl sm:text-3xl font-bold text-foreground">{stats.totalVehicles}</div>
          </CardContent>
        </Card>

        <Card className="border-border transition-colors duration-200 animate-fadeIn delay-200" data-testid="active-vehicles-card">
          <CardHeader className="flex flex-row items-center justify-between pb-1 sm:pb-2 p-3 sm:p-6">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">{t('dashboard.vehicles.active')}</CardTitle>
            <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 text-green-500" />
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
            <div className="text-2xl sm:text-3xl font-bold text-green-500">{stats.activeVehicles}</div>
          </CardContent>
        </Card>

        <Card className="border-border transition-colors duration-200 animate-fadeIn delay-300" data-testid="maintenance-card">
          <CardHeader className="flex flex-row items-center justify-between pb-1 sm:pb-2 p-3 sm:p-6">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">{t('dashboard.vehicles.maintenance')}</CardTitle>
            <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4 text-amber-500" />
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
            <div className="text-2xl sm:text-3xl font-bold text-amber-500">{stats.maintenanceVehicles}</div>
          </CardContent>
        </Card>

        <Card className="border-border transition-colors duration-200 animate-fadeIn delay-400" data-testid="tuv-expiring-card">
          <CardHeader className="flex flex-row items-center justify-between pb-1 sm:pb-2 p-3 sm:p-6">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">{t('dashboard.tuv.expiring')}</CardTitle>
            <Calendar className="h-3 w-3 sm:h-4 sm:w-4 text-red-500" />
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
            <div className="text-2xl sm:text-3xl font-bold text-red-500">{stats.tuvExpiring}</div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Vehicles */}
      <Card className="border-border transition-colors duration-200 animate-slideIn delay-100">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-lg sm:text-xl font-bold text-foreground">{t('dashboard.myVehicles')}</CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          <div className="space-y-3 sm:space-y-4">
            {vehicles.slice(0, 5).map(vehicle => {
              const tuvDate = new Date(vehicle.tuv_expiry);
              const now = new Date();
              const daysUntilTuv = Math.ceil((tuvDate - now) / (1000 * 60 * 60 * 24));
              
              return (
                <div 
                  key={vehicle.vehicle_id} 
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 rounded-lg bg-card border border-border hover:bg-accent/50 transition-all duration-200 gap-3 sm:gap-4"
                  data-testid={`vehicle-${vehicle.vehicle_id}`}
                >
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <Truck className="h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground" />
                    </div>
                    <div>
                      <div className="font-bold text-sm sm:text-base text-foreground">{vehicle.license_plate}</div>
                      <div className="text-xs sm:text-sm text-muted-foreground">{vehicle.model}</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4 pl-13 sm:pl-0">
                    <div className="text-left sm:text-right">
                      <div className="text-xs text-muted-foreground">Kilometerstand</div>
                      <div className="font-mono text-sm sm:text-base text-foreground">{vehicle.current_km.toLocaleString()} km</div>
                    </div>
                    <div className="text-left sm:text-right">
                      <div className="text-xs text-muted-foreground">TÜV</div>
                      <div className={`font-medium text-sm sm:text-base ${daysUntilTuv < 30 ? 'text-red-500' : 'text-foreground'}`}>
                        {daysUntilTuv} Tage
                      </div>
                    </div>
                    <Badge 
                      variant={vehicle.status === 'in_use' ? 'default' : vehicle.status === 'maintenance' ? 'destructive' : 'secondary'}
                      className="capitalize text-xs whitespace-nowrap"
                    >
                      {vehicle.status === 'in_use' ? t('dashboard.status.inUse') : vehicle.status === 'maintenance' ? t('dashboard.status.maintenance') : t('dashboard.status.available')}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Push Notifications Settings */}
      <PushNotificationToggle />
    </div>
  );
};

export default Dashboard;
